// src/pages/TrackCreation/Step1.tsx
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import shell from "../../pages/track/create/createShell.module.css";

interface Subcategory {
    name: string;
    step: string;
}

interface SidebarItem {
    title: string;
    subcategories?: Subcategory[];
    hasSubcategories: boolean;
    // If no subcategories, an optional step can be provided
    step?: string;
}

const QuizSidebar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    // Replaces the "quiz-*" segment in the current URL with a new step.
    const replaceStepInUrl = (newStep: string): string => {
        return location.pathname.replace(/quiz-[^/]+/, newStep);
    };

    // Extracts a value from the URL using a regex pattern.
    // For example, it matches URLs like "/create/tour/<...>/quiz-<...>/<...>/<value>"
    const extractValueFromPattern = (): string | null => {
        const regex = /^\/create\/tour\/[^/]+\/quiz-[^/]+\/[^/]+\/([^/]+)$/;
        const match = location.pathname.match(regex);
        return match ? match[1] : null;
    };

    // Determine whether additional subcategories should be shown.
    const additionalSubcategories = extractValueFromPattern() !== null;

    // Sidebar structure
    const sidebarItems: SidebarItem[] = [
        {
            title: t("quiz"),
            hasSubcategories: true,
            subcategories: [
                { name: t("language"), step: "quiz-language" },
                ...(additionalSubcategories
                    ? [
                          { name: t("content"), step: "quiz-title" },
                          { name: t("coordinates"), step: "quiz-coordinates" },
                          { name: t("pictures"), step: "quiz-picture" },
                      ]
                    : []),
            ],
        },
    ];

    const handleBackClick = (): void => {
        const newPath = location.pathname.replace(/\/quiz-[^/]+\/.+$/, "/quiz-content");
        navigate(newPath);
    };

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, step: string): void => {
        // Add any custom click handling logic if needed.
        // For now, this is a placeholder.
    };

    return (
        <div className={`track-create-sidebar-navigation ${shell.navRoot}`}>
            <ul className={`track-create-sidebar-nav ${shell.navList}`}>
                {sidebarItems.map((item, index) => (
                    <div key={index} className={shell.navGroup}>
                        {item.hasSubcategories && item.subcategories ? (
                            <>
                                <li className={`sidebar-title ${shell.navHeading}`}>{item.title}</li>
                                <ul className={`subcategory-list ${shell.navSubList}`}>
                                    {item.subcategories.map((sub, subIndex) => (
                                        <li
                                            key={subIndex}
                                            className={`${shell.navSubItem} ${
                                                location.pathname.includes(sub.step) ? shell.navSubItemActive : ""
                                            }`}
                                        >
                                            <Link
                                                to={replaceStepInUrl(sub.step)}
                                                className="sidebar-link"
                                                onClick={(e) => handleLinkClick(e, sub.step)}
                                            >
                                                {sub.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        ) : (
                            <li
                                className={`sidebar-title ${shell.navTitleLink} ${
                                    location.pathname.includes(item.step || "") ? shell.navTitleLinkActive : ""
                                }`}
                            >
                                <Link
                                    to={replaceStepInUrl(item.step || "")}
                                    className="sidebar-link"
                                    onClick={(e) => handleLinkClick(e, item.step || "")}
                                >
                                    {item.title}
                                </Link>
                            </li>
                        )}
                    </div>
                ))}
            </ul>
            <Button
                label={t("backToGeneral")}
                variant="outline"
                shape="pill"
                className={shell.navBackBtn}
                onClick={handleBackClick}
            />
        </div>
    );
};

export default QuizSidebar;
